import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";

const ImageCarousel = ({ images, onClose, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev, onClose]);

  if (!images || images.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center" data-testid="carousel-modal">
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white/60 hover:text-white z-10"
      >
        <X className="w-8 h-8" />
      </button>
      <button
        onClick={() => {
          const img = images[currentIndex];
          const link = document.createElement('a');
          link.href = `${API}/images/${img.id}/raw`;
          link.download = `image-${img.id?.slice(0, 8) || currentIndex}.png`;
          link.click();
          toast.success('Slika preuzeta!');
        }}
        className="absolute top-6 right-20 text-white/60 hover:text-green-400 z-10 transition-colors"
        data-testid="carousel-download"
        title="Preuzmi sliku"
      >
        <Download className="w-8 h-8" />
      </button>

      <div className="absolute top-6 left-6 text-white/60 text-sm">
        {currentIndex + 1} / {images.length}
      </div>

      {images.length > 1 && (
        <>
          <button
            onClick={goPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={goNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </>
      )}

      <div className="max-w-[90vw] max-h-[85vh] flex items-center justify-center">
        <img
          src={`${API}/images/${images[currentIndex].id}/raw`}
          alt={`Image ${currentIndex + 1}`}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
        />
      </div>

      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[80vw] p-2">
          {images.map((img, idx) => (
            <button
              key={img.id}
              onClick={() => setCurrentIndex(idx)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex ? 'border-primary' : 'border-transparent opacity-50 hover:opacity-100'
              }`}
            >
              <img
                src={`${API}/images/${img.id}/raw`}
                alt={`Thumb ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;
